import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiEdit2, FiTrash2, FiCornerDownRight } from "react-icons/fi";
import Notification, { showToast } from "../../components/common/notification/Notification";
import ReviewCard from "../../components/common/formkit/ReviewCard";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import { createLeadService } from "./services/leadService";
import { LEAD_STATUSES } from "./constants/leadEndpoints";
import "../../components/common/formkit/formkit.css";
import "../lease/LeasesPage.css";
import { formatDateTime as formatDateTimeSafe } from "../../utils/date";

const formatDateTime = (value) => formatDateTimeSafe(value, { fallback: "" });
const errorText = (err, fallback) => err?.response?.data?.text || err?.message || fallback;

/** One note with its replies, an inline reply box, and edit/delete for the author's own notes. */
const NoteThread = ({ note, replies, byParent, currentUserId, busy, onReply, onEdit, onDelete, depth = 0 }) => {
  const [mode, setMode] = useState(null); // 'reply' | 'edit'
  const [text, setText] = useState("");
  const isMine = Number(note.user?.id) === Number(currentUserId);
  const author = [note.user?.first_name, note.user?.last_name].filter(Boolean).join(" ") || "User";

  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    const ok = mode === "edit" ? await onEdit(note, value) : await onReply(note.id, value);
    if (ok) {
      setMode(null);
      setText("");
    }
  };

  return (
    <div style={{ marginLeft: depth ? 24 : 0 }}>
      <div className="border rounded-3 p-3 bg-white">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="min-w-0">
            <div className="fw-semibold small">{author}</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{note.note}</div>
            {note.created_on && <div className="text-muted small mt-1">{formatDateTime(note.created_on)}</div>}
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setMode("reply"); setText(""); }} aria-label="Reply to note">
              <FiCornerDownRight size={14} />
            </button>
            {isMine && (
              <>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setMode("edit"); setText(note.note); }} aria-label="Edit note">
                  <FiEdit2 size={14} />
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete(note)} aria-label="Delete note">
                  <FiTrash2 size={14} />
                </button>
              </>
            )}
          </div>
        </div>
        {mode && (
          <form onSubmit={submit} className="d-flex flex-column gap-2 mt-3">
            <textarea
              className="fk-input"
              rows={2}
              style={{ height: "auto" }}
              aria-label={mode === "edit" ? "Edit note" : "Write a reply"}
              placeholder={mode === "edit" ? "Edit your note" : "Reply..."}
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMode(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !text.trim()}>
                {mode === "edit" ? "Update" : "Reply"}
              </button>
            </div>
          </form>
        )}
      </div>
      {replies.length > 0 && (
        <div className="d-flex flex-column gap-2 mt-2">
          {replies.map((reply) => (
            <NoteThread
              key={reply.id}
              note={reply}
              replies={byParent[reply.id] || []}
              byParent={byParent}
              currentUserId={currentUserId}
              busy={busy}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** /customer/leads/:Id - lead details with notes (add, reply, edit, delete) like mobile */
const LeadDetailPage = () => {
  const { Id } = useParams();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId = authUser?.user?.id || authUser?.id || authUser?.userDetails?.id;

  const apiClient = usePropertyApiClient();
  const leadService = useMemo(() => createLeadService(apiClient), [apiClient]);

  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notesError, setNotesError] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadNotes = useCallback(async () => {
    try {
      setNotesError(null);
      const data = await leadService.listNotes(Id);
      setNotes(data?.customerLeadNotes || []);
    } catch (err) {
      setNotesError(errorText(err, "Unable to load notes."));
    }
  }, [leadService, Id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadService.getById(Id);
      setLead(data?.customerLead || null);
      await loadNotes();
    } catch (err) {
      setError(errorText(err, "Unable to load this lead."));
    } finally {
      setLoading(false);
    }
  }, [leadService, Id, loadNotes]);

  useEffect(() => {
    load();
  }, [load]);

  // Notes form a tree through parent_id (0 = top level)
  const { roots, byParent } = useMemo(() => {
    const map = {};
    notes.forEach((n) => {
      const key = n.parent_id || 0;
      (map[key] = map[key] || []).push(n);
    });
    return { roots: map[0] || [], byParent: map };
  }, [notes]);

  const runNoteAction = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) showToast(successMessage, { type: "success" });
      await loadNotes();
      return true;
    } catch (err) {
      showToast(errorText(err, "Action failed"), { type: "error" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    const value = noteText.trim();
    if (!value) return;
    if (await runNoteAction(() => leadService.addNote(Id, value), "Note added")) setNoteText("");
  };

  const handleDeleteNote = (note) => {
    if (window.confirm("Delete this note?")) runNoteAction(() => leadService.removeNote(note.id), "Note deleted");
  };

  const handleDeleteLead = async () => {
    setBusy(true);
    try {
      await leadService.remove(Id);
      showToast("Lead deleted successfully", { type: "success" });
      navigate("/customer/leads", { replace: true });
    } catch (err) {
      showToast(errorText(err, "Unable to delete lead"), { type: "error" });
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }} aria-busy="true">
        <div className="fk-skel" style={{ height: 28, width: "40%", marginBottom: 16 }} />
        <div className="fk-skel" style={{ height: 220 }} />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error || "This lead could not be found."}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={load}>
              Retry
            </button>
            <Link to="/customer/leads" className="btn btn-sm btn-outline-secondary">
              Back to My Leads
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const status = LEAD_STATUSES.find((s) => s.value === String(lead.status || "").toUpperCase());

  return (
    <div className="fk-page" style={{ maxWidth: 860 }}>
      <Notification />
      <div className="fk-header d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="fk-title">{lead.title}</h1>
          <p className="fk-subtitle">
            {formatDateTime(lead.created_on)}
            {status && <span className={`badge bg-${status.variant} ms-2${status.variant === "warning" ? " text-dark" : ""}`}>{status.label}</span>}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link to={`/customer/leads/${Id}/edit`} className="btn btn-outline-primary d-inline-flex align-items-center gap-2">
            <FiEdit2 size={15} /> Edit
          </Link>
          <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-2" onClick={() => setConfirmDelete(true)}>
            <FiTrash2 size={15} /> Delete
          </button>
        </div>
      </div>

      <div className="fk-review-list">
        <ReviewCard
          title="Lead details"
          rows={[
            { label: "Type", value: lead.purpose?.name },
            { label: "Budget", value: lead.budget != null ? `₹${Number(lead.budget).toLocaleString("en-IN")}` : "" },
            { label: "City", value: lead.city },
            { label: "State", value: lead.state },
            { label: "Description", value: lead.description || "No description available", full: true },
          ]}
        />

        <ReviewCard title="Notes">
          <form onSubmit={addNote} className="d-flex flex-column gap-2 mb-3">
            <textarea
              className="fk-input"
              rows={2}
              style={{ height: "auto" }}
              aria-label="Write a note"
              placeholder="Write a note..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !noteText.trim()}>
                Add Note
              </button>
            </div>
          </form>

          {notesError ? (
            <div className="alert alert-danger d-flex align-items-center justify-content-between mb-0" role="alert">
              <span>{notesError}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadNotes}>
                Retry
              </button>
            </div>
          ) : roots.length === 0 ? (
            <p className="text-muted mb-0">No notes yet.</p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {roots.map((note) => (
                <NoteThread
                  key={note.id}
                  note={note}
                  replies={byParent[note.id] || []}
                  byParent={byParent}
                  currentUserId={currentUserId}
                  busy={busy}
                  onReply={(parentId, value) => runNoteAction(() => leadService.addNote(Id, value, parentId), "Reply added")}
                  onEdit={(n, value) => runNoteAction(() => leadService.editNote(n, value), "Note updated")}
                  onDelete={handleDeleteNote}
                />
              ))}
            </div>
          )}
        </ReviewCard>
      </div>

      {confirmDelete && (
        <div className="lease-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Delete lead">
          <div className="lease-confirm">
            <h5 className="fw-bold">Delete lead?</h5>
            <p className="text-muted mb-4">"{lead.title}" will be removed from your leads.</p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteLead} disabled={busy}>
                {busy ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetailPage;
